/** Anfragenübersicht (F19/F20): eigene Meldungen, für Kundenadmin/Reviewer
 * zusätzlich die Organisations-Sicht. Karten-Liste nach Design-Handoff (Screen 02). */

import { useEffect, useMemo, useRef, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { CalendarDays, ChevronRight, MessageCircle, Plus, RotateCcw, Trash2 } from "lucide-react"
import { Link } from "react-router-dom"
import { toast } from "sonner"
import { chatGesehene } from "@/components/meldung/MeldungsChat"
import { api, apiError } from "@/services/api"
import {
  datumKurz,
  PRIO_BADGE,
  PRIO_LABEL,
  STATUS_FARBE,
  STATUS_LABEL,
  STATUS_PUNKT,
  TYP_LABEL,
} from "@/lib/labels"
import { useAuthStore } from "@/store/auth"
import type { Report, ReportStatus } from "@/types"

/** „Offene" Meldungen = aktive Pipeline (ohne Entwürfe und Abschluss-Stati). */
const OFFENE_STATI: ReportStatus[] = ["neu", "gesichtet", "freigegeben", "in_umsetzung"]

/** Chip-Reihenfolge (logische Pipeline). „entwurf" nur in der eigenen Sicht. */
const STATUS_REIHENFOLGE: ReportStatus[] = [
  "entwurf",
  "neu",
  "gesichtet",
  "freigegeben",
  "in_umsetzung",
  "erledigt",
  "abgelehnt",
  "verworfen",
]

/** Wie weit der Datumsstrahl zurückreicht (Tage ab heute). */
const STRAHL_TAGE = 120

const _WOCHENTAG = new Intl.DateTimeFormat("de-DE", { weekday: "short" })
const _MONAT = new Intl.DateTimeFormat("de-DE", { month: "short" })

function tagBeginn(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate())
}

function gleicherTag(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}

/** Halboffenes Tages-Zeitfenster [von, bis) in ISO/UTC aus einem lokalen Tag. */
function tagesGrenzen(d: Date): { von: string; bis: string } {
  const von = tagBeginn(d)
  const bis = new Date(von)
  bis.setDate(bis.getDate() + 1)
  return { von: von.toISOString(), bis: bis.toISOString() }
}

function MeldungsKarte({ report, orgSicht, appName }: {
  report: Report
  orgSicht: boolean
  appName: string | null
}) {
  const istEntwurf = report.status === "entwurf"
  const queryClient = useQueryClient()
  const loeschen = useMutation({
    mutationFn: () => api.reports.remove(report.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["reports"] })
      toast.success("Entwurf gelöscht")
    },
    onError: (e) => toast.error(apiError(e)),
  })
  const meta = istEntwurf
    ? `noch nicht abgesendet · zuletzt bearbeitet ${datumKurz(report.createdAt)}`
    : [
        report.typ ? TYP_LABEL[report.typ] : null,
        orgSicht && report.melderName ? `gemeldet von ${report.melderName}` : null,
        datumKurz(report.createdAt),
        appName,
      ]
        .filter(Boolean)
        .join(" · ")

  return (
    <Link
      to={istEntwurf ? `/melden?entwurf=${report.id}` : `/meldungen/${report.id}`}
      className={`flex items-center gap-4 rounded-xl px-5 py-4 transition-colors ${
        istEntwurf
          ? "border border-dashed bg-card/60 hover:bg-card"
          : "border bg-card shadow-card hover:border-primary/30"
      }`}
    >
      <span className={`h-2 w-2 shrink-0 rounded-full ${STATUS_PUNKT[report.status]}`} />
      <div className="min-w-0 flex-1">
        <div className={`truncate text-[15px] font-semibold ${istEntwurf ? "text-muted-foreground" : ""}`}>
          {report.forgejoIssueNr != null && (
            // Karte ist selbst ein <Link> — daher kein verschachteltes <a>,
            // sondern Klick-Handler, der die Karten-Navigation unterdrückt
            <span
              role="link"
              title="Forgejo-Issue öffnen"
              className={`mr-1.5 font-semibold text-primary ${report.forgejoIssueUrl ? "hover:underline" : ""}`}
              onClick={(e) => {
                if (!report.forgejoIssueUrl) return
                e.preventDefault()
                e.stopPropagation()
                window.open(report.forgejoIssueUrl, "_blank", "noopener")
              }}
            >
              #{report.forgejoIssueNr}
            </span>
          )}
          {report.titel || "ohne Titel"}
        </div>
        <div className="mt-0.5 truncate text-[13px] text-muted-foreground">{meta}</div>
      </div>
      {/* F39: Ungelesen-Badge — neue Chat-Nachrichten seit dem letzten Öffnen */}
      {report.kommentareAnzahl > chatGesehene(report.id) && (
        <span
          title="Neue Chat-Nachrichten"
          className="flex shrink-0 items-center gap-1 rounded-full bg-primary px-2.5 py-1 text-xs font-bold text-primary-foreground"
        >
          <MessageCircle className="h-3 w-3" />
          {report.kommentareAnzahl - chatGesehene(report.id)}
        </span>
      )}
      {!istEntwurf && (
        <span
          className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-bold ${PRIO_BADGE[report.prioritaet]}`}
        >
          {PRIO_LABEL[report.prioritaet]}
        </span>
      )}
      {istEntwurf && (
        <button
          type="button"
          title="Entwurf löschen"
          disabled={loeschen.isPending}
          onClick={(e) => {
            // Karte ist ein <Link> — Navigation unterdrücken
            e.preventDefault()
            e.stopPropagation()
            if (window.confirm("Diesen Entwurf endgültig löschen?")) loeschen.mutate()
          }}
          className="shrink-0 rounded-lg p-1.5 text-muted-foreground/70 hover:bg-destructive/10 hover:text-destructive disabled:opacity-50"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      )}
      <span
        className={`inline-flex w-[104px] shrink-0 justify-center rounded-full px-2.5 py-1 text-xs font-bold ${STATUS_FARBE[report.status]}`}
      >
        {STATUS_LABEL[report.status]}
      </span>
      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground/50" />
    </Link>
  )
}

export function UebersichtPage() {
  const istOrgSicht = useAuthStore((s) => s.isKundenadmin() || s.isReviewer())
  const [scope, setScope] = useState<"mine" | "org">("mine")
  const [statusFilter, setStatusFilter] = useState<ReportStatus[]>(OFFENE_STATI)

  // Klick vs. Doppelklick unterscheiden: der Einzelklick wird kurz verzögert;
  // ein eintreffender Doppelklick storniert ihn (isolieren statt umschalten).
  const klickTimer = useRef<number | null>(null)
  const umschalten = (s: ReportStatus) =>
    setStatusFilter((akt) => (akt.includes(s) ? akt.filter((x) => x !== s) : [...akt, s]))
  const chipKlick = (s: ReportStatus) => {
    if (klickTimer.current != null) {
      window.clearTimeout(klickTimer.current)
      klickTimer.current = null
      return
    }
    klickTimer.current = window.setTimeout(() => {
      klickTimer.current = null
      umschalten(s)
    }, 220)
  }
  const chipDoppelklick = (s: ReportStatus) => {
    if (klickTimer.current != null) {
      window.clearTimeout(klickTimer.current)
      klickTimer.current = null
    }
    setStatusFilter([s]) // isolieren: alle anderen abwählen
  }

  // Entwürfe gibt es nur in der eigenen Sicht (Org-Scope blendet sie aus).
  const sichtbareStati = STATUS_REIHENFOLGE.filter((s) => s !== "entwurf" || scope === "mine")
  const istDefault =
    statusFilter.length === OFFENE_STATI.length &&
    OFFENE_STATI.every((s) => statusFilter.includes(s))

  // Datumsstrahl: ein/aus + gewählter Tag. Beim Einschalten immer auf heute.
  const [datumsstrahlAn, setDatumsstrahlAn] = useState(false)
  const [gewaehlterTag, setGewaehlterTag] = useState<Date>(() => tagBeginn(new Date()))
  const heuteRef = useRef<HTMLButtonElement>(null)

  const tage = useMemo(() => {
    const heute = tagBeginn(new Date())
    return Array.from({ length: STRAHL_TAGE + 1 }, (_, i) => {
      const d = new Date(heute)
      d.setDate(d.getDate() - (STRAHL_TAGE - i))
      return d
    })
  }, [])

  function datumsstrahlUmschalten() {
    setDatumsstrahlAn((an) => {
      if (!an) setGewaehlterTag(tagBeginn(new Date())) // beim Einschalten auf heute
      return !an
    })
  }

  // Beim Einschalten den heutigen Tag in den sichtbaren Bereich scrollen
  useEffect(() => {
    if (datumsstrahlAn) {
      requestAnimationFrame(() =>
        heuteRef.current?.scrollIntoView({ inline: "center", block: "nearest" })
      )
    }
  }, [datumsstrahlAn])

  const grenzen = datumsstrahlAn ? tagesGrenzen(gewaehlterTag) : null

  const { data, isLoading, error } = useQuery({
    queryKey: ["reports", scope, datumsstrahlAn ? "alle" : statusFilter, grenzen?.von ?? null],
    queryFn: () =>
      api.reports.list({
        scope,
        // Datumsstrahl überschreibt den Status-Filter: an diesem Tag alle Stati
        ...(datumsstrahlAn ? {} : { status: statusFilter }),
        ...(grenzen ? { created_from: grenzen.von, created_to: grenzen.bis } : {}),
      }),
    enabled: datumsstrahlAn || statusFilter.length > 0,
  })

  // Anwendungs-Hinweis nur bei mehreren Anwendungen der Instanz
  const { data: anwendungen } = useQuery({
    queryKey: ["applications"],
    queryFn: api.applications.list,
  })
  const appName = new Map((anwendungen ?? []).map((a) => [a.id, a.name]))
  const mehrereApps = (anwendungen?.length ?? 0) > 1

  return (
    <div>
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="mb-1.5 text-[26px] font-bold tracking-tight">Meldungen</h1>
          <p className="text-sm text-muted-foreground">
            Alle Fehler und Änderungswünsche, die du oder deine Organisation erfasst habt.
          </p>
        </div>
        <Link
          to="/melden"
          className="flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground hover:bg-primary-hover"
        >
          <Plus className="h-4 w-4" />
          Neue Meldung
        </Link>
      </div>

      {istOrgSicht && (
        <div className="mb-5 inline-flex rounded-[10px] bg-secondary p-[3px]">
          {(
            [
              ["mine", "Meine Meldungen"],
              ["org", "Organisation"],
            ] as const
          ).map(([wert, label]) => (
            <button
              key={wert}
              type="button"
              onClick={() => setScope(wert)}
              className={`rounded-lg px-4 py-1.5 text-[13px] font-semibold transition-colors ${
                scope === wert
                  ? "bg-card text-foreground shadow-card"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {/* Status-Filter: Default = offene; Klick schaltet um, Doppelklick isoliert.
          Bei aktivem Datumsstrahl inaktiv (der Strahl zeigt alle Stati des Tages). */}
      <div className="mb-5 flex flex-wrap items-center gap-1.5">
        <span
          className="mr-1 text-[13px] font-medium text-muted-foreground"
          title={datumsstrahlAn ? "Bei aktivem Datumsstrahl deaktiviert" : undefined}
        >
          Status:
        </span>
        {sichtbareStati.map((s) => {
          const aktiv = statusFilter.includes(s)
          return (
            <button
              key={s}
              type="button"
              disabled={datumsstrahlAn}
              onClick={() => chipKlick(s)}
              onDoubleClick={() => chipDoppelklick(s)}
              title={
                datumsstrahlAn
                  ? "Bei aktivem Datumsstrahl deaktiviert"
                  : "Klick: an/aus · Doppelklick: nur diesen Status"
              }
              aria-pressed={aktiv}
              className={`inline-flex select-none items-center gap-1.5 rounded-full border-2 px-3 py-1 text-xs font-semibold transition-colors ${
                datumsstrahlAn ? "cursor-not-allowed opacity-40" : ""
              } ${
                aktiv
                  ? "border-primary bg-primary/10 text-foreground"
                  : "border-transparent bg-secondary text-muted-foreground hover:text-foreground"
              }`}
            >
              <span className={`h-2 w-2 rounded-full ${STATUS_PUNKT[s]} ${aktiv ? "" : "opacity-40"}`} />
              {STATUS_LABEL[s]}
            </button>
          )
        })}
        {!istDefault && !datumsstrahlAn && (
          <button
            type="button"
            onClick={() => setStatusFilter(OFFENE_STATI)}
            className="ml-1 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium text-muted-foreground hover:text-foreground"
          >
            <RotateCcw className="h-3 w-3" />
            Nur offene
          </button>
        )}
        {datumsstrahlAn && (
          <span className="ml-1 text-[11px] italic text-muted-foreground">
            zeigt alle Stati des Tages
          </span>
        )}
        <button
          type="button"
          onClick={datumsstrahlUmschalten}
          aria-pressed={datumsstrahlAn}
          title="Datumsstrahl ein/aus — filtert nach Erstellungstag"
          className={`ml-auto inline-flex items-center gap-1.5 rounded-full border-2 px-3 py-1 text-xs font-semibold transition-colors ${
            datumsstrahlAn
              ? "border-primary bg-primary/10 text-foreground"
              : "border-transparent bg-secondary text-muted-foreground hover:text-foreground"
          }`}
        >
          <CalendarDays className="h-3.5 w-3.5" />
          Datumsstrahl
        </button>
      </div>

      {/* Datumsstrahl: horizontal scrollend; Klick wählt den Erstellungstag */}
      {datumsstrahlAn && (
        <div className="mb-5 flex gap-1.5 overflow-x-auto rounded-xl border bg-card p-2">
          {tage.map((d) => {
            const istHeute = gleicherTag(d, new Date())
            const gewaehlt = gleicherTag(d, gewaehlterTag)
            return (
              <button
                key={d.toISOString()}
                ref={istHeute ? heuteRef : undefined}
                type="button"
                onClick={() => setGewaehlterTag(d)}
                className={`flex shrink-0 flex-col items-center rounded-lg px-3 py-1.5 leading-tight transition-colors ${
                  gewaehlt
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                }`}
              >
                <span className="text-[11px] font-medium opacity-80">{_WOCHENTAG.format(d)}</span>
                <span className="text-sm font-bold">{d.getDate()}</span>
                <span className="text-[11px] opacity-80">{_MONAT.format(d)}</span>
                {istHeute && (
                  <span
                    className={`mt-0.5 h-1 w-1 rounded-full ${gewaehlt ? "bg-primary-foreground" : "bg-primary"}`}
                  />
                )}
              </button>
            )
          })}
        </div>
      )}

      {statusFilter.length === 0 && (
        <div className="rounded-xl border bg-card px-6 py-12 text-center text-muted-foreground">
          Kein Status ausgewählt.{" "}
          <button
            type="button"
            onClick={() => setStatusFilter(OFFENE_STATI)}
            className="text-primary underline"
          >
            Offene Meldungen anzeigen
          </button>
        </div>
      )}

      {isLoading && <p className="text-muted-foreground">Lade Meldungen …</p>}

      {error != null && (
        <p className="rounded-md bg-destructive/10 px-4 py-3 text-destructive">
          {apiError(error)}
        </p>
      )}

      {data && data.total === 0 && (
        <div className="rounded-xl border bg-card px-6 py-12 text-center text-muted-foreground">
          {datumsstrahlAn ? (
            <>
              Keine Meldungen vom {datumKurz(gewaehlterTag.toISOString())}.
              <br />
              <button
                type="button"
                onClick={() => setGewaehlterTag(tagBeginn(new Date()))}
                className="text-sm text-primary underline"
              >
                Zurück zu heute
              </button>
            </>
          ) : istDefault ? (
            <>
              Keine offenen Meldungen.
              <br />
              <Link to="/melden" className="text-sm text-primary underline">
                Neue Meldung erfassen
              </Link>
            </>
          ) : (
            <>
              Keine Meldungen im gewählten Status.
              <br />
              <button
                type="button"
                onClick={() => setStatusFilter(OFFENE_STATI)}
                className="text-sm text-primary underline"
              >
                Zurück zu „nur offene“
              </button>
            </>
          )}
        </div>
      )}

      {data && data.total > 0 && (
        <div className="flex flex-col gap-2.5">
          {data.data.map((report) => (
            <MeldungsKarte
              key={report.id}
              report={report}
              orgSicht={scope === "org"}
              appName={
                mehrereApps ? appName.get(report.applicationId) ?? String(report.applicationId) : null
              }
            />
          ))}
        </div>
      )}
    </div>
  )
}
